import { format, isToday, isYesterday, parseISO } from 'date-fns';
import { Button, CardView, rem, ToolTip } from 'design-system';
import BlurAmountView from 'design-system/BlurAmountView';
import Text from 'design-system/Text';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { useMainStackNavigation } from 'utils/types/navigationV6';
import { UnavailableOnWebModal } from 'utils/packages/UnavailableOnWebModal';
import { formatDayMonth } from 'utils';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import useColors from 'hooks/useColors';
import { Tier } from 'features/premium/entities';
import { TransUnionHeader } from 'features/rent-reporting/svg/TransUnionHeader';
import { isWeb } from '../../../constants';
import CreditScoreChart from './CreditScoreChart';
import { markCreditScoreAnimationConsumed } from '../actions';
import { selectCreditScore, selectCreditScoreHasRealTimeUpdates, selectIsCreditScoreAvailable, selectLastSeenCreditScore, selectShouldAnimateCreditScore, selectShouldShowCreditScoreHistory, } from '../selectors';
import useDefaultScoreBands from '../hooks/useDefaultScoreBands';
import { formatNextScoreUpdateLabel, formatNextScoreUpdateTooltip, } from '../utils';
const ONBOARDING_PREVIEW_SCORE = 630;
const ONBOARDING_PREVIEW_DIAMETER = 140;
const strings = {
    title: 'Check your credit score and report',
    subTitle: 'Get your reports, alerts, and personalised tips to help your score grow',
    buttonText: 'Check my score',
    sinceLastUpdate: 'since last update',
    sinceYesterday: 'since yesterday',
    points: 'points',
    toolTipTitle: 'Credit score update',
    toolTipUpgrade: 'Upgrade',
    toolTipGotIt: 'Got it',
    toolTipOkay: 'Okay',
    toolTipUltimateSubtitle: 'Your credit score and report update whenever changes are detected',
};
const formatChange = (change) => {
    if (change === 0) {
        return `±${Math.abs(change)}`;
    }
    return `${change < 0 ? '-' : '+'}${Math.abs(change)}`;
};
const OnboardingCard = memo(({ onPress }) => (<CardView style={[styles.onboardingCard, styles.bottomPadding]} noMarginTop>
    <View style={styles.onboardingContent}>
      <Text Text-16>{strings.title}</Text>
      <View style={styles.onboardingBody}>
        <View style={styles.onboardingSubTitle}>
          <Text TextThin-14 Secondary>
            {strings.subTitle}
          </Text>
        </View>
        <View style={styles.onboardingChart}>
          <CreditScoreChart score={ONBOARDING_PREVIEW_SCORE} diameter={ONBOARDING_PREVIEW_DIAMETER} withoutLabels withoutAnimation noVerticalPadding/>
        </View>
      </View>
    </View>
    <Button brandLight square medium title={strings.buttonText} onPress={onPress}/>
  </CardView>));
const CreditScoreCardContent = memo(({ data }) => {
    const navigation = useMainStackNavigation();
    const dispatch = useAppDispatch();
    const isFocused = useIsFocused();
    const shouldAnimate = useAppSelector(selectShouldAnimateCreditScore);
    const lastSeenScore = useAppSelector(selectLastSeenCreditScore);
    const hasRealTimeUpdates = useAppSelector(selectCreditScoreHasRealTimeUpdates);
    const shouldShowCreditScoreHistory = useAppSelector(selectShouldShowCreditScoreHistory);
    const scrambled = useAppSelector((store) => store.utils.scrambled);
    const colors = useColors();
    const { score, change, lastChange, lastChangeDate, scoreDate, nextUpdateDate, } = data;
    const [animationStarted, setAnimationStarted] = useState(false);
    const [animationFromScore, setAnimationFromScore] = useState(null);
    useEffect(() => {
        if (!animationStarted && isFocused && shouldAnimate) {
            setAnimationFromScore(lastSeenScore);
            setAnimationStarted(true);
            dispatch(markCreditScoreAnimationConsumed());
        }
    }, [animationStarted, isFocused, shouldAnimate, lastSeenScore, dispatch]);
    const nextUpdateTooltip = useMemo(() => formatNextScoreUpdateTooltip(nextUpdateDate), [nextUpdateDate]);
    const formattedUpdate = useMemo(() => {
        if (hasRealTimeUpdates) {
            const date = parseISO(scoreDate);
            let dayLabel;
            if (isToday(date)) {
                dayLabel = 'today';
            }
            else if (isYesterday(date)) {
                dayLabel = 'yesterday';
            }
            else {
                dayLabel = format(date, 'd MMM');
            }
            return `Updated ${dayLabel} at ${format(date, 'HH:mm')}`;
        }
        return formatNextScoreUpdateLabel(nextUpdateDate);
    }, [hasRealTimeUpdates, scoreDate, nextUpdateDate]);
    // lastChange/lastChangeDate are only meaningful as a pair — when either is
    // missing (no change in the last 3 months, or an older backend), fall back
    // to the neutral ±0 with the generic label.
    const displayChange = lastChange !== null && lastChangeDate !== null ? lastChange : change ?? 0;
    const changeLabel = useMemo(() => {
        if (lastChange == null || lastChangeDate == null) {
            return strings.sinceLastUpdate;
        }
        const date = parseISO(lastChangeDate);
        if (isToday(date)) {
            return strings.sinceLastUpdate;
        }
        if (isYesterday(date)) {
            return strings.sinceYesterday;
        }
        return `since ${formatDayMonth(date)}`;
    }, [lastChange, lastChangeDate]);
    const changeDirectionProps = displayChange > 0 ? { Positive: true } : { Negative: true };
    const changeTextProps = displayChange
        ? changeDirectionProps
        : { color: colors.text.statusOffline };
    const changeLabelProps = { color: colors.text.secondary };
    const onUpgrade = useCallback(() => {
        navigation.navigate('EmmaProModal', {
            tier: Tier.ultimate,
        });
    }, []);
    const onPressCreditScoreHistory = useCallback(() => {
        navigation.navigate('CreditScoreHistoryScreen');
    }, []);
    const scoreBands = useDefaultScoreBands();
    return (<CardView style={styles.cardContainer} noMarginTop>
      <View style={styles.logoContainer}>
        <TransUnionHeader width={76} height={20} color={colors.text.secondary}/>
      </View>
      <CreditScoreChart key={animationStarted ? 'animated' : 'static'} score={score} fromScore={(animationStarted ? animationFromScore : lastSeenScore) ?? undefined} scoreBands={scoreBands} chartScale="nonLinear" withoutAnimation={!animationStarted} noVerticalPadding/>
      <View style={styles.chartSubContainer}>
        <View style={styles.changeContainer}>
          <BlurAmountView enabled={scrambled} containerStyle={styles.changeBlurContainer}>
            <Text Numbers-16 centered {...changeTextProps}>
              {formatChange(displayChange)}{' '}
              <Text Text-16 {...changeTextProps}>
                {strings.points}
              </Text>
            </Text>
          </BlurAmountView>
          <Text TextThin-16 centered {...changeLabelProps}>
            {changeLabel}
          </Text>
        </View>
        <View style={styles.updateContainer}>
          <Text TextThin-14 Secondary centered>
            {formattedUpdate}
          </Text>
          {hasRealTimeUpdates ? (<ToolTip title={strings.toolTipTitle} subtitle={strings.toolTipUltimateSubtitle} small tootTipBtnText={strings.toolTipOkay}/>) : (<ToolTip title={strings.toolTipTitle} subtitle={nextUpdateTooltip} small toolTipExtraBtnText={strings.toolTipGotIt} tootTipBtnText={strings.toolTipUpgrade} onBtnPress={onUpgrade}/>)}
        </View>
      </View>
      {!!shouldShowCreditScoreHistory && (<Button custom brandLight square title="View score history" onPress={onPressCreditScoreHistory} style={styles.creditScoreHistoryButton}/>)}
    </CardView>);
});
const CheckCreditScoreCard = () => {
    const navigation = useMainStackNavigation();
    const isCreditScoreAvailable = useAppSelector(selectIsCreditScoreAvailable);
    const creditScoreData = useAppSelector(selectCreditScore);
    const handleStartOnboarding = useCallback(() => {
        if (isWeb) {
            UnavailableOnWebModal.show('emma://borrow', 'Check my score');
            return;
        }
        navigation.navigate('CreditScoreOnboarding');
    }, [navigation]);
    if (!isCreditScoreAvailable) {
        return null;
    }
    if (!creditScoreData) {
        return <OnboardingCard onPress={handleStartOnboarding}/>;
    }
    return <CreditScoreCardContent data={creditScoreData}/>;
};
const cardBase = {
    padding: rem(16),
    marginTop: rem(8),
};
const styles = StyleSheet.create({
    onboardingCard: { ...cardBase, gap: rem(20) },
    cardContainer: {
        ...cardBase,
        paddingTop: rem(40),
        gap: rem(16),
    },
    bottomPadding: {
        marginBottom: rem(24),
    },
    onboardingContent: {
        gap: rem(4),
        flex: 1,
    },
    onboardingBody: {
        gap: rem(16),
        flex: 1,
        flexDirection: 'row',
    },
    onboardingSubTitle: {
        flex: 1,
    },
    onboardingChart: {
        justifyContent: 'flex-end',
        marginTop: rem(12),
    },
    updateContainer: {
        flexDirection: 'row',
        gap: rem(4),
        justifyContent: 'center',
        alignItems: 'center',
    },
    chartSubContainer: {
        paddingBottom: rem(8),
    },
    changeContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        gap: rem(4),
    },
    changeBlurContainer: {
        transform: [{ translateY: rem(0.5) }],
    },
    creditScoreHistoryButton: {
        marginBottom: rem(8),
    },
    logoContainer: {
        position: 'absolute',
        top: rem(18),
        right: rem(16),
    },
});
export default CheckCreditScoreCard;
